import { EventFilter, IEvent } from '@mirage-projects/communication-library';

export enum STEP_ACTION_TYPE {
    SEND = 'SEND',
    RECEIVE = 'RECEIVE',
}

export type EventType =
    | 'SET CURRENT_STATION RESPONSE'
    | 'GET INSTRUMENT_HEIGHT PROCESS_STARTED'
    | 'GET INSTRUMENT_HEIGHT PROCESS_PROGRESS'
    | 'GET INSTRUMENT_HEIGHT PROCESS_FINISHED'
    | 'GET INSTRUMENT_HEIGHT RESPONSE'
    | 'SET LAYOUT_NAVIGATION_VIEW RESPONSE'
    | 'GET LAYOUT_POINT REQUEST'
    | 'GET MULTI_SCAN_CLOUD PROCESS_STARTED'
    | 'GET MULTI_SCAN_CLOUD PROCESS_PROGRESS'
    | 'GET MULTI_SCAN_CLOUD PROCESS_FINISHED'
    | 'GET MULTI_SCAN_CLOUD REQUEST'
    | 'GET MULTI_SCAN_CLOUD RESPONSE'
    | 'CREATE NEW_STATION PROCESS_STARTED'
    | 'CREATE NEW_STATION PROCESS_FINISHED'
    | 'CREATE NEW_STATION REQUEST'
    | 'CREATE NEW_STATION RESPONSE'
    | 'SET PRISM_POLE_POSITION RESPONSE'
    | 'GET PROJECT_DATA REQUEST'
    | 'GET PROJECT_DATA RESPONSE'
    | 'GET REPORT_PICK_POINT REQUEST'
    | 'GET REPORT_PICK_POINT RESPONSE'
    | 'GET RCP_EXPORT REQUEST'
    | 'GET RCP_EXPORT RESPONSE'
    | 'SET RIGHT_COLUMN_SIZE RESPONSE'
    | 'GET SCAN_CLOUD PROCESS_FINISHED'
    | 'GET SCAN_CLOUD PROCESS_PROGRESS'
    | 'GET SCAN_CLOUD PROCESS_STARTED'
    | 'GET SCAN_CLOUD REQUEST'
    | 'GET SCAN_CLOUD RESPONSE'
    | 'GET SCAN_CLOUD_AND_RESECTION PROCESS_STARTED'
    | 'GET SCAN_CLOUD_AND_RESECTION PROCESS_PROGRESS'
    | 'GET SCAN_CLOUD_AND_RESECTION PROCESS_FINISHED'
    | 'GET SCAN_CLOUD_AND_RESECTION REQUEST'
    | 'GET SCAN_CLOUD_AND_RESECTION RESPONSE'
    | 'GET SCAN_TARGET PROCESS_FINISHED'
    | 'GET SCAN_TARGET PROCESS_PROGRESS'
    | 'GET SCAN_TARGET PROCESS_STARTED'
    | 'GET SCAN_TARGET REQUEST'
    | 'GET SCAN_TARGET RESPONSE'
    | 'GET SCAN_TARGET2 REQUEST'
    | 'GET SCAN_TARGET2 RESPONSE'
    | 'GET SCANNER_STATE REQUEST'
    | 'GET SCANNER_STATE RESPONSE'
    | 'SET SCANNER_STATE RESPONSE'
    | 'GET STANDARD_VERIFICATION PROCESS_STARTED'
    | 'GET STANDARD_VERIFICATION PROCESS_PROGRESS'
    | 'GET STANDARD_VERIFICATION PROCESS_FINISHED'
    | 'GET STANDARD_VERIFICATION REQUEST'
    | 'GET STANDARD_VERIFICATION RESPONSE'
    | 'GET STATION_POSITION REQUEST'
    | 'GET STATION_POSITION RESPONSE'
    | 'GET STATIONS_TRANSFORMATIONS REQUEST'
    | 'GET STATIONS_TRANSFORMATIONS RESPONSE'
    | 'GET TOTALSTATION_VERIFICATION PROCESS_STARTED'
    | 'GET TOTALSTATION_VERIFICATION PROCESS_PROGRESS'
    | 'GET TOTALSTATION_VERIFICATION PROCESS_FINISHED'
    | 'GET TOTALSTATION_VERIFICATION REQUEST'
    | 'GET TOTALSTATION_VERIFICATION RESPONSE'
    | 'GET TRANSFER_STATION_DATA REQUEST'
    | 'GET TRANSFER_STATION_DATA RESPONSE'
    | 'GET TRANSFER_STATION_DATA_QUEUED RESPONSE'
    | 'GET USER_PREFERENCES REQUEST'
    | 'GET USER_PREFERENCES RESPONSE'
    | 'GET WINDOW_SCAN_CLOUD PROCESS_STARTED'
    | 'GET WINDOW_SCAN_CLOUD PROCESS_PROGRESS'
    | 'GET WINDOW_SCAN_CLOUD PROCESS_FINISHED'
    | 'GET WINDOW_SCAN_CLOUD REQUEST'
    | 'GET WINDOW_SCAN_CLOUD RESPONSE'
    | 'SET WORKFLOW_STATE RESPONSE'
    | 'ACTION GET_FILE_CONTENTS RESPONSE'
    | 'ACTION COPY_AND_COMPRESS_FILES RESPONSE';

export const SCANNER_TYPES = ['GLS', 'GTL', 'GT', 'Raster'] as const;

export type ScannerType = (typeof SCANNER_TYPES)[number];

export interface EventSubscriptionsData {
    stationUuid: string;
    success: true;
    target: TargetData;
}

export interface ScanTargetEventSubscriptionsData {
    message: EventSubscriptionsData;
    origin: string;
    type: string;
    uuid: string;
    created: Date;
}

export type TargetData = {
    name: string;
    coordinates: [number, number, number];
    height: number;
    type: string;
    prismCorrection: number;
};

export interface ResectionPair {
    referencePoint: { name: string };
    target: TargetData;
    isCorrect: boolean;
}

export type ResectionPairs = [ResectionPair, ResectionPair];

export interface GlobalData {
    stationUuid?: string;
    state?: string;
    scannerType?: ScannerType;
    stationUuidList?: string[];
    queryList?: string[];
    stationName?: string;
    target?: TargetData;
    isCorrect?: boolean;
    prismPos?: [number, number, number];
    projects?: Record<string, any>;
    content?: Record<string, any>;
    filePath?: string;
    fileSize?: string;
    success?: boolean;
}

export interface StepEventMsg {
    success?: boolean;
    state?: string;
    type?: string;
    progress?: number;
    message?: any;
    stationUuidList?: string[];
    enabled?: boolean;
    reportPointFeatures?: boolean;
    height?: number;
    queryList?: string[];
    stationUuid?: string;
    projectUuid?: string;
    stationName?: string;
    method?: string;
    target?: Record<string, unknown>;
    isCorrect?: boolean;
    finished?: boolean;
    resectionPairs?: ResectionPairs;
    pairsMatched?: boolean;
    coordinates?: [number, number, number];
    finishedScan?: FinishedScan;
    FileFormatID?: string;
    DateTime?: string;
    UnitLength?: string;
    CommandGUID?: string;
    CommandName?: string;
    InputDirectory?: string;
    OutputDirectory?: string;
    AnalysisInstanceCollection?: AnalysisInstance[];
}

export interface FinishedScan {
    scanUuid: string;
    instanceGUID: string;
}

export interface AnalysisInstance {
    InstanceGUID: string;
    InstallationStatus: InstallationStatusType;
    Conformance: ConformanceType;
    ScanCoverage: number;
    AsBuiltTransform: AsBuiltTransform;
    HeatMapInformation: HeatMapInformation;
    Deviation: Deviation;
}

export interface AsBuiltTransform {
    Matrix: number[];
    IsIdentity: boolean;
}

export interface HeatMapInformation {
    PTSFilePath: string;
    HeatMapFilePath: string;
}

export interface Deviation {
    AsDesignedTranslation: number;
    AsDesignedVerticalRotation: number;
    AsDesignedHorizontalRotation: number;
    RotationFromHorizontal: number;
    HorizontalDisplacement: number;
    LongAxisDisplacement: number;
    RotationFromVertical: number;
    TwistRotation: number;
    VerticalDisplacement: number;
    XDisplacement: number;
    YDisplacement: number;
}

export type InstallationStatusType = 'Installed' | 'Not Installed' | string;

export type ConformanceType = 'In Tolerance' | 'Out Of Tolerance' | string;

export interface StepEventData {
    type: EventType;
    message?: StepEventMsg;
}

export type StepEventFactory = (params: ParamObj, globalData?: GlobalData, prevData?: GlobalData) => StepEventData;

export type StepEvent = StepEventData | StepEventFactory;

export function isStepEventData(event: StepEvent): event is StepEventData {
    return typeof event === 'object' && 'type' in event;
}

export function isStepEventFactory(event: StepEvent): event is StepEventFactory {
    return typeof event === 'function';
}

export type MockData = GlobalData | StepEventData | IEvent;

export type ResolveConditionFunction = (value: IEvent, data?: GlobalData) => boolean;

export type RunnerConditionParamFunction = (...args: any[]) => Promise<MockData[]>;
export type RunnerConditionFunction = (fn: RunnerConditionParamFunction) => Promise<MockData[]>;
export type RunnerCondition = RunnerConditionFunction | EventType | EventFilter;

export interface StepData extends Omit<IEvent, 'type'> {
    type?: EventType;
    action: STEP_ACTION_TYPE;
    runDelay?: number;
    eventFilter?: EventType;
    event?: StepEvent;
    resolveCondition?: ResolveConditionFunction;
}

export interface ProcessData {
    name: string;
    steps: StepData[];
}

export interface ParamObj {
    message?: string;
    resectionPairs?: ResectionPairs;
    [key: string]: unknown;
}
